import { assertNever } from '~/util';
import type { Basis as _Basis } from '../pulser-schema/types';

// this enum values must match the basis representation in simulation worker
export type Basis = _Basis;

export const Basis = {
  GroundRydberg: 'ground-rydberg',
  Digital: 'digital',
  XY: 'XY'
} as const satisfies Record<string, Basis>;

export type Addressing = 'local' | 'global';

export type ChannelKind = 'raman' | 'rydberg' | 'microwave';

export type Channel = {
  uuid: string;
  label: string;
  basis: Basis;
  addressing: Addressing;
  /** Maximum possible detuning (in rad/µs), in absolute value. */
  maxAbsDetuning: number;
  /** Maximum pulse amplitude (in rad/µs).*/
  maxAmp: number;
  /** Time taken to change the phase between consecutive pulses (in ns). */
  phaseJumpTime: number;
  /** The duration of a clock cycle (in ns). The duration of a pulse or delay instruction is enforced to be a multiple of the clock cycle. */
  clockPeriod: number;
  /** The shortest duration an instruction can take. // Notes: What is the measurement unit? */
  minDuration: number;
  /** Minimum time required between the ends of two target instructions (in ns). */
  minRetargetInterval?: number;
  /** Time take to change the target (in ns) */
  fixedRetargetT?: number;
  /** How many qubits can be addressed at once by the same beam. */
  maxTargets?: number;
  /** The longest duration an instruction can take. */
  maxDuration?: number;
  /** The modulation bandwidth at -3dB (50% redution), in MHz. */
  modBandwidth?: number;
  /** Enables detuning pulses in the channel */
  enableDetuning: boolean;
};

export function hasXyChannel(channels: readonly { basis: Basis }[]): boolean {
  return channels.some((channel) => channel.basis === Basis.XY);
}

/**
 * Get basis part of hardware channel label text.
 */
export function getChannelKind(ch: { basis: Basis }): ChannelKind {
  const basis = ch.basis;
  switch (basis) {
    case Basis.Digital:
      return 'raman';
    case Basis.GroundRydberg:
      return 'rydberg';
    case Basis.XY:
      return 'microwave';
    default:
      assertNever(
        basis,
        `Channel basis ${basis} is not yet supported. Currently only 'digital', 'ground-rydberg', and 'xy' channels are supported.`
      );
  }
}
